// NodeEdgeService.js

/**
 * NodeEdgeService.js
 *
 * Central place for Firestore edge reads/writes.
 */

// Firebase
import {
	collection,
	doc,
	getDocs,
	onSnapshot,
	query,
	where,
	writeBatch,
} from 'firebase/firestore';
import { collections, db, functions, httpsCallable } from 'firebaseConfig';

// Utilities
import { chunkArray } from 'utilities/Arrays';

// Constants
const MAX_BATCH_OPS = 450;

const linkNodesCallable = httpsCallable(functions, 'linkNodes');
const linkNodesBatchCallable = httpsCallable(functions, 'linkNodesBatch');
const unlinkNodesCallable = httpsCallable(functions, 'unlinkNodes');

const LINK_NODES_BATCH_MAX = 100;
const UPSERT_EDGES_FALLBACK_CONCURRENCY = 6;

// Detects non-empty strings
const isNonEmptyString = (value) => typeof value === 'string' && value.trim().length > 0;
const normalizeBaseKey = (value) => (isNonEmptyString(value) ? value.trim() : null);

function isMissingCallableError(error) {
	const code = String(error?.code || '').trim().toLowerCase();
	return (
		code === 'functions/not-found'
		|| code === 'functions/unimplemented'
		|| code === 'not-found'
		|| code === 'unimplemented'
	);
}

/**
 * Run async work over items with a fixed concurrency pool.
 *
 * @template T, R
 * @param {T[]} items
 * @param {number} concurrency
 * @param {(item: T, index: number) => Promise<R>} worker
 * @returns {Promise<R[]>}
 */
async function mapWithConcurrency(items, concurrency, worker) {
	const list = Array.isArray(items) ? items : [];
	if (!list.length) return [];

	const limit = Math.max(1, Number(concurrency) || 1);
	const results = new Array(list.length);
	let nextIndex = 0;

	async function runWorker() {
		while (nextIndex < list.length) {
			const currentIndex = nextIndex;
			nextIndex += 1;
			results[currentIndex] = await worker(list[currentIndex], currentIndex);
		}
	}

	const workers = Array.from(
		{ length: Math.min(limit, list.length) },
		() => runWorker(),
	);
	await Promise.all(workers);
	return results;
}

function buildAdjacentEdgeQuery(edgesRef, fieldName, nodeKey, baseKey = null) {
	const normalizedBaseKey = normalizeBaseKey(baseKey);
	if (normalizedBaseKey) {
		// Writers store `baseKey` (linkNodes); query that field (indexed).
		return query(
			edgesRef,
			where(fieldName, '==', nodeKey),
			where('baseKey', '==', normalizedBaseKey)
		);
	}

	return query(edgesRef, where(fieldName, '==', nodeKey));
}

/**
 * Returns canonical ordered edge endpoints for undirected semantics.
 *
 * @param {string} nodeKeyA
 * @param {string} nodeKeyB
 * @returns {{ sourceNodeKey: string, targetNodeKey: string }}
 */
export function canonicalizeEdge(nodeKeyA, nodeKeyB) {
	if (!isNonEmptyString(nodeKeyA) || !isNonEmptyString(nodeKeyB)) {
		throw new Error('canonicalizeEdge: both node keys are required');
	}

	const left = nodeKeyA.trim();
	const right = nodeKeyB.trim();

	if (left === right) {
		throw new Error('canonicalizeEdge: self-edges are not allowed');
	}

	if (left < right) {
		return { sourceNodeKey: left, targetNodeKey: right };
	}

	return { sourceNodeKey: right, targetNodeKey: left };
}

/**
 * Stable deterministic edge document id.
 *
 * @param {string} nodeKeyA
 * @param {string} nodeKeyB
 * @returns {string}
 */
export function buildEdgeId(nodeKeyA, nodeKeyB) {
	const { sourceNodeKey, targetNodeKey } = canonicalizeEdge(nodeKeyA, nodeKeyB);
	return `${sourceNodeKey}__${targetNodeKey}`;
}

/**
 * Creates or updates an edge document.
 *
 * @param {string} nodeKeyA
 * @param {string} nodeKeyB
 * @param {Object} options
 * @param {string|null} [options.baseKey]
 * @param {string|null} [options.parentKey] - Hierarchical parent endpoint (Project→Task, Task→Task).
 * @param {boolean} [options.skipActivity=false] - Skip "added a …" relationship Activity on create.
 */
export async function upsertEdge(nodeKeyA, nodeKeyB, options = {}) {
	const {
		baseKey = null,
		parentKey = null,
		skipActivity = false,
	} = options || {};
	const { sourceNodeKey, targetNodeKey } = canonicalizeEdge(nodeKeyA, nodeKeyB);

	const payload = {
		nodeKeyA: sourceNodeKey,
		nodeKeyB: targetNodeKey,
		baseKey,
	};
	const normalizedParentKey = isNonEmptyString(parentKey) ? parentKey.trim() : '';
	if (normalizedParentKey) {
		payload.parentKey = normalizedParentKey;
	}
	if (skipActivity === true) {
		payload.skipActivity = true;
	}

	const result = await linkNodesCallable(payload);

	return result?.data || null;
}

/**
 * Creates or updates many edges in as few round trips as possible.
 * Prefers `linkNodesBatch`; falls back to concurrent `linkNodes` when
 * the batch callable is not deployed yet.
 *
 * @param {Array<{ nodeKeyA: string, nodeKeyB: string, baseKey?: string|null, parentKey?: string|null, skipActivity?: boolean }>} links
 * @param {Object} [options]
 * @param {string|null} [options.baseKey]
 * @param {boolean} [options.skipActivity=false]
 * @param {boolean} [options.freshChildren=false] - Skip hierarchy-conflict reads (new children only).
 * @returns {Promise<object[]>}
 */
export async function upsertEdges(links = [], options = {}) {
	const {
		baseKey = null,
		skipActivity = false,
		freshChildren = false,
	} = options || {};

	const normalizedLinks = (Array.isArray(links) ? links : [])
		.map((entry) => {
			const nodeKeyA = isNonEmptyString(entry?.nodeKeyA) ? entry.nodeKeyA.trim() : '';
			const nodeKeyB = isNonEmptyString(entry?.nodeKeyB) ? entry.nodeKeyB.trim() : '';
			if (!nodeKeyA || !nodeKeyB || nodeKeyA === nodeKeyB) return null;

			const payload = {
				nodeKeyA,
				nodeKeyB,
				baseKey: normalizeBaseKey(entry?.baseKey) || normalizeBaseKey(baseKey),
			};
			const parentKey = isNonEmptyString(entry?.parentKey) ? entry.parentKey.trim() : '';
			if (parentKey) payload.parentKey = parentKey;
			if (skipActivity === true || entry?.skipActivity === true) {
				payload.skipActivity = true;
			}
			return payload;
		})
		.filter(Boolean);

	if (!normalizedLinks.length) return [];

	const chunks = chunkArray(normalizedLinks, LINK_NODES_BATCH_MAX);
	const results = [];

	for (const chunk of chunks) {
		try {
			const response = await linkNodesBatchCallable({
				baseKey: normalizeBaseKey(baseKey) || chunk[0]?.baseKey || null,
				freshChildren: freshChildren === true,
				links: chunk,
				skipActivity: skipActivity === true,
			});
			const batchResults = Array.isArray(response?.data?.results)
				? response.data.results
				: [];
			results.push(...batchResults);
		} catch (error) {
			if (!isMissingCallableError(error)) {
				throw error;
			}

			// Batch callable not deployed yet — parallel single links.
			const fallbackResults = await mapWithConcurrency(
				chunk,
				UPSERT_EDGES_FALLBACK_CONCURRENCY,
				async (payload) => {
					const response = await linkNodesCallable(payload);
					return response?.data || null;
				},
			);
			results.push(...fallbackResults.filter(Boolean));
		}
	}

	return results;
}

/**
 * Deletes a specific edge between two nodes.
 *
 * @param {string} nodeKeyA
 * @param {string} nodeKeyB
 */
export async function deleteEdge(nodeKeyA, nodeKeyB, options = {}) {
	const { baseKey = null } = options || {};
	const { sourceNodeKey, targetNodeKey } = canonicalizeEdge(nodeKeyA, nodeKeyB);

	await unlinkNodesCallable({
		nodeKeyA: sourceNodeKey,
		nodeKeyB: targetNodeKey,
		baseKey,
	});
}

/**
 * Lists adjacent node keys for a node from `_edges`.
 *
 * @param {string} nodeKey
 * @returns {Promise<string[]>}
 */
export async function listAdjacentNodeKeys(nodeKey, options = {}) {
	if (!isNonEmptyString(nodeKey)) {
		throw new Error('listAdjacentNodeKeys: nodeKey is required');
	}

	const key = nodeKey.trim();
	const { baseKey = null } = options || {};
	const normalizedBaseKey = normalizeBaseKey(baseKey);
	if (!normalizedBaseKey) {
		// Membership-scoped edge rules require a base filter.
		return [];
	}
	const edgesRef = collection(db, collections.edges);

	const [sourceSnap, targetSnap] = await Promise.all([
		getDocs(buildAdjacentEdgeQuery(edgesRef, 'sourceNodeKey', key, normalizedBaseKey)),
		getDocs(buildAdjacentEdgeQuery(edgesRef, 'targetNodeKey', key, normalizedBaseKey)),
	]);

	let sourceDocs = sourceSnap.docs;
	let targetDocs = targetSnap.docs;

	// Match server listAdjacentNodeKeys: if base-scoped queries miss legacy edges
	// (missing/mismatched baseKey), fall back to unscoped adjacency reads.
	if (normalizedBaseKey && sourceDocs.length === 0 && targetDocs.length === 0) {
		const [legacySource, legacyTarget] = await Promise.all([
			getDocs(query(edgesRef, where('sourceNodeKey', '==', key))),
			getDocs(query(edgesRef, where('targetNodeKey', '==', key))),
		]);
		sourceDocs = legacySource.docs;
		targetDocs = legacyTarget.docs;
	}

	const neighbors = new Set();

	sourceDocs.forEach((docSnap) => {
		const data = docSnap.data() || {};
		if (isNonEmptyString(data.targetNodeKey)) {
			neighbors.add(data.targetNodeKey.trim());
		}
	});

	targetDocs.forEach((docSnap) => {
		const data = docSnap.data() || {};
		if (isNonEmptyString(data.sourceNodeKey)) {
			neighbors.add(data.sourceNodeKey.trim());
		}
	});

	return Array.from(neighbors);
}

/**
 * Real-time listener for adjacent node keys.
 *
 * @param {string} nodeKey
 * @param {(neighborKeys: string[]) => void} callback
 * @returns {() => void}
 */
export function subscribeAdjacentNodeKeys(nodeKey, callback, options = {}) {
	if (!isNonEmptyString(nodeKey)) {
		throw new Error('subscribeAdjacentNodeKeys: nodeKey is required');
	}
	if (typeof callback !== 'function') {
		throw new Error('subscribeAdjacentNodeKeys: callback must be a function');
	}

	const key = nodeKey.trim();
	const { baseKey = null } = options || {};
	const normalizedBaseKey = normalizeBaseKey(baseKey);
	if (!normalizedBaseKey) {
		callback([]);
		return () => {};
	}
	const edgesRef = collection(db, collections.edges);

	let sourceRows = [];
	let targetRows = [];

	const emit = () => {
		const neighbors = new Set();

		sourceRows.forEach((data) => {
			if (isNonEmptyString(data.targetNodeKey)) {
				neighbors.add(data.targetNodeKey.trim());
			}
		});

		targetRows.forEach((data) => {
			if (isNonEmptyString(data.sourceNodeKey)) {
				neighbors.add(data.sourceNodeKey.trim());
			}
		});

		callback(Array.from(neighbors));
	};

	const unsubSource = onSnapshot(
		buildAdjacentEdgeQuery(edgesRef, 'sourceNodeKey', key, normalizedBaseKey),
		(snapshot) => {
			sourceRows = snapshot.docs.map((docSnap) => docSnap.data() || {});
			emit();
		},
		(error) => {
			console.error('[NodeEdgeService] subscribeAdjacentNodeKeys source listener error:', error);
		}
	);

	const unsubTarget = onSnapshot(
		buildAdjacentEdgeQuery(edgesRef, 'targetNodeKey', key, normalizedBaseKey),
		(snapshot) => {
			targetRows = snapshot.docs.map((docSnap) => docSnap.data() || {});
			emit();
		},
		(error) => {
			console.error('[NodeEdgeService] subscribeAdjacentNodeKeys target listener error:', error);
		}
	);

	return () => {
		unsubSource();
		unsubTarget();
	};
}

/**
 * Lists hierarchical child node keys for a parent (edges with parentKey set).
 *
 * @param {string} parentNodeKey
 * @param {Object} [options]
 * @param {string|null} [options.baseKey]
 * @returns {Promise<string[]>}
 */
export async function listHierarchicalChildKeys(parentNodeKey, options = {}) {
	if (!isNonEmptyString(parentNodeKey)) {
		throw new Error('listHierarchicalChildKeys: parentNodeKey is required');
	}

	const parentKey = parentNodeKey.trim();
	const { baseKey = null } = options || {};
	const normalizedBaseKey = normalizeBaseKey(baseKey);
	if (!normalizedBaseKey) return [];

	const edgesRef = collection(db, collections.edges);
	const snap = await getDocs(
		query(
			edgesRef,
			where('parentKey', '==', parentKey),
			where('baseKey', '==', normalizedBaseKey),
		),
	);

	const children = new Set();
	snap.docs.forEach((docSnap) => {
		const data = docSnap.data() || {};
		const source = isNonEmptyString(data.sourceNodeKey) ? data.sourceNodeKey.trim() : '';
		const target = isNonEmptyString(data.targetNodeKey) ? data.targetNodeKey.trim() : '';
		if (parentKey === source && target) children.add(target);
		else if (parentKey === target && source) children.add(source);
	});

	return Array.from(children);
}

/**
 * For a set of node keys, resolve hierarchical parentKey per child from incident edges.
 *
 * @param {string[]} nodeKeys
 * @param {Object} [options]
 * @param {string|null} [options.baseKey]
 * @returns {Promise<Record<string, string>>} childKey → parentKey
 */
export async function listHierarchyParentsForNodeKeys(nodeKeys, options = {}) {
	const keys = Array.from(
		new Set(
			(Array.isArray(nodeKeys) ? nodeKeys : [])
				.map((key) => String(key || '').trim())
				.filter(Boolean),
		),
	);
	if (keys.length === 0) return {};

	const { baseKey = null } = options || {};
	const normalizedBaseKey = normalizeBaseKey(baseKey);
	if (!normalizedBaseKey) return {};

	const edgesRef = collection(db, collections.edges);
	const parentByChild = {};

	const chunks = chunkArray(keys, 30);
	for (const chunk of chunks) {
		const snaps = await Promise.all(
			chunk.flatMap((key) => ([
				getDocs(buildAdjacentEdgeQuery(edgesRef, 'sourceNodeKey', key, normalizedBaseKey)),
				getDocs(buildAdjacentEdgeQuery(edgesRef, 'targetNodeKey', key, normalizedBaseKey)),
			])),
		);

		snaps.forEach((snap) => {
			snap.docs.forEach((docSnap) => {
				const data = docSnap.data() || {};
				const parentKey = isNonEmptyString(data.parentKey) ? data.parentKey.trim() : '';
				if (!parentKey) return;
				const source = isNonEmptyString(data.sourceNodeKey) ? data.sourceNodeKey.trim() : '';
				const target = isNonEmptyString(data.targetNodeKey) ? data.targetNodeKey.trim() : '';
				if (!source || !target) return;
				if (parentKey !== source && parentKey !== target) return;
				const childKey = parentKey === source ? target : source;
				if (keys.includes(childKey)) {
					parentByChild[childKey] = parentKey;
				}
			});
		});
	}

	return parentByChild;
}

/**
 * Deletes all incident edges for a node.
 *
 * @param {string} nodeKey
 */
export async function deleteIncidentEdges(nodeKey) {
	if (!isNonEmptyString(nodeKey)) {
		throw new Error('deleteIncidentEdges: nodeKey is required');
	}

	const key = nodeKey.trim();
	const edgesRef = collection(db, collections.edges);

	const [sourceSnap, targetSnap] = await Promise.all([
		getDocs(query(edgesRef, where('sourceNodeKey', '==', key))),
		getDocs(query(edgesRef, where('targetNodeKey', '==', key))),
	]);

	const docIds = Array.from(
		new Set([
			...sourceSnap.docs.map((docSnap) => docSnap.id),
			...targetSnap.docs.map((docSnap) => docSnap.id),
		])
	);

	if (docIds.length === 0) return;

	const chunks = chunkArray(docIds, MAX_BATCH_OPS);
	for (const chunk of chunks) {
		const batch = writeBatch(db);
		chunk.forEach((edgeId) => {
			batch.delete(doc(db, collections.edges, edgeId));
		});
		await batch.commit();
	}
}
